import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Period } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  formatRestrictionValueUtility,
  type RestrictionValue,
} from 'lumen/utils/channel-pricing/restrictions';
import {
  RESTRICTION_TYPES,
  type RestrictionModelContextsType,
} from 'lumen/utils/constants/channel-pricing-constants';
import { isHotelLevel } from 'lumen/utils/pricing-assistant/restriction-action-utils';

export default class RestrictionRecommendation extends Model {
  [Type] = 'restriction-recommendation' as const;

  /* Belongs to period rather than restriction: a recommendation can exist when no restriction is currently in place, so there is no restriction record to link to. See: https://lighthouseintelligence.atlassian.net/wiki/x/WQB_hAE */
  @belongsTo('period', { async: false, inverse: 'restrictionRecommendations' })
  declare period: Period;

  @attr('string') declare channelId: string;
  @attr('string')
  declare context: RestrictionModelContextsType;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare message: string;
  @attr('string') declare otaId: string;

  @attr('string') declare rateGroupId: string | null;
  @attr('string') declare rateId: string;
  @attr('number') declare subscriptionId: number;
  @attr('json') declare values: RestrictionValue[];

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValues() {
    return this.values.map((restrictionValueObject) => {
      return {
        ...restrictionValueObject,
        ...this.formatRestrictionValue(restrictionValueObject),
      };
    });
  }

  get isForMinorChannel(): boolean {
    return this.channelId === 'OTHER';
  }

  get isHotelLevel(): boolean {
    return isHotelLevel(this);
  }

  /* Applied MinLos value (Hotel vs. Deep discount rate group) */
  get minLosAppliedContext(): RestrictionModelContextsType | null {
    return this.minLosRestrictionValueObject?.appliedContext ?? null;
  }

  get minLosRestrictionValueObject(): RestrictionValue | undefined {
    return this.values.find(
      (value) => value.type === RESTRICTION_TYPES.MIN_LOS,
    );
  }

  get minLosValue(): number | null {
    return this.minLosRestrictionValueObject?.value ?? null;
  }

  get overbookingValue(): number | null {
    return (
      this.values.find((value) => value.type === RESTRICTION_TYPES.OVERBOOKING)
        ?.value ?? null
    );
  }

  // SMART DISTRIBUTION TODO: can we move the formatRestrictionValue to a util later? Seems to be reusable in both restriction and restriction-recommendation model
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRestrictionValue(restrictionValueObject: RestrictionValue) {
    return formatRestrictionValueUtility(restrictionValueObject, this.intl);
  }
}
